import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { CopyButton, Modal } from './ui';

/**
 * QR display for wallet addresses — a small trigger button that opens a modal
 * with a scannable code, so an address on the desktop dashboard can be paid
 * from a phone wallet without typing anything.
 *
 * The QR encodes the RAW address (no chain-specific URI scheme): every mobile
 * wallet's scanner accepts a bare address, while `ethereum:`-style URIs are
 * rejected by non-EVM wallets.
 */
export function QrButton({
  value,
  title,
  subtitle,
}: {
  value: string;
  title: string;
  subtitle?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)} title="Show QR code">
        QR
      </button>
      {open && <QrModal value={value} title={title} subtitle={subtitle} onClose={() => setOpen(false)} />}
    </>
  );
}

function QrModal({
  value,
  title,
  subtitle,
  onClose,
}: {
  value: string;
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    QRCode.toDataURL(value, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: 'M',
      // Fixed palette so the code stays scannable regardless of app theme.
      color: { dark: '#14181d', light: '#ffffff' },
    })
      .then(setDataUrl)
      .catch(() => setDataUrl(null));
  }, [value]);

  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ textAlign: 'center' }}>
        {subtitle && (
          <p className="muted" style={{ marginTop: 0 }}>
            {subtitle}
          </p>
        )}

        {dataUrl ? (
          <img
            src={dataUrl}
            alt={`QR code for ${value}`}
            width={280}
            height={280}
            style={{ borderRadius: 8, border: '1px solid var(--border)' }}
          />
        ) : (
          <div className="empty">Generating…</div>
        )}

        <div
          className="secret-reveal"
          style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span style={{ flex: 1, textAlign: 'left' }}>{value}</span>
          <CopyButton value={value} />
        </div>
      </div>
    </Modal>
  );
}
